import { areUploadsPaused, isRateLimitError } from '@/api/rateLimits'
import { ApiError } from '@/api/ApiError'
import { keys } from '@/api/queryKeys.ts'
import { queryClient } from '@/api/queryClient.ts'
import {
  createPendingAudioFile,
  markUploadEnded,
} from '@/features/files/api/createFile.ts'
import {
  IndexedDbChunkStore,
  UploadStreamManager,
} from '@/features/recordings/recorder'
import { StoredRecording } from '@/features/recordings/recorder/types.ts'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { IDBPDatabase } from 'idb'
import { RecorderDatabaseSchema } from '@/features/recordings/recorder/IndexedDbChunkStore'

const chunkStore = new IndexedDbChunkStore()
const uploadManager = new UploadStreamManager(chunkStore)

let storageReadyPromise: Promise<IDBPDatabase<RecorderDatabaseSchema>> | null =
  null

const ensureStorageReady = () => {
  if (!storageReadyPromise) {
    storageReadyPromise = chunkStore.openDatabase().catch((error) => {
      storageReadyPromise = null
      throw error
    })
  }
  return storageReadyPromise
}

const uploadsInProgress = new Set<string>()

const uploadRecording = async (recordingId: string) => {
  const storeState = useLocalRecordingsStore.getState()
  const recording = storeState.getRecordingById(recordingId)
  if (!recording) return
  if (recording.chunkCount <= 0) {
    storeState.removeRecording(recordingId)
    return
  }
  if (uploadsInProgress.has(recordingId)) {
    return
  }

  try {
    uploadsInProgress.add(recordingId)

    storeState.updateRecording(recordingId, {
      status: 'uploading',
      uploadProgress: recording.finalizationFileId ? 100 : 0,
      uploadError: null,
    })

    await ensureStorageReady()

    let fileId = recording.finalizationFileId
    if (!fileId) {
      const pendingFile = await createPendingAudioFile({
        filename: recording.filename,
        durationSeconds: Math.max(0, recording.durationMs / 1000),
        createdAt: new Date(recording.createdAt).toISOString(),
        source: recording.source,
        language: recording.language,
      })

      await uploadManager.uploadRecording({
        recordingId,
        url: pendingFile.policy,
        method: 'PUT',
        totalBytes: recording.totalBytes,
        contentType: recording.mimeType || 'audio/webm',
        headers:
          pendingFile.acl === null
            ? {}
            : {
                'X-amz-acl': pendingFile.acl ?? 'private',
              },
        onProgress: (progress) => {
          useLocalRecordingsStore.getState().updateRecording(recordingId, {
            status: 'uploading',
            uploadProgress: progress.percent,
            uploadError: null,
          })
        },
      })
      fileId = pendingFile.id
      useLocalRecordingsStore
        .getState()
        .updateRecording(recordingId, { finalizationFileId: fileId })
    }
    try {
      await markUploadEnded(fileId)
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        useLocalRecordingsStore
          .getState()
          .updateRecording(recordingId, { finalizationFileId: undefined })
      }
      throw error
    }

    await chunkStore.clearRecording(recordingId)
    useLocalRecordingsStore.getState().removeRecording(recordingId)
    queryClient.invalidateQueries({
      queryKey: [keys.files],
    })
  } catch (error) {
    // Rate-limited uploads go back to the queue, which is paused until the user resumes.
    const requeue =
      isRateLimitError(error) ||
      (error instanceof DOMException && error.name === 'AbortError')
    useLocalRecordingsStore.getState().updateRecording(recordingId, {
      status: requeue ? 'stopped' : 'upload_failed',
      uploadError: requeue || !(error instanceof Error) ? null : error.message,
    })
  } finally {
    uploadsInProgress.delete(recordingId)
    checkUploads()
  }
}

export const createLocalFileFromChunkStore = async (
  recordingId: string
): Promise<File> => {
  await ensureStorageReady()

  const recording = useLocalRecordingsStore
    .getState()
    .getRecordingById(recordingId)
  if (!recording) {
    throw new Error('Recording not found')
  }

  const chunks: Blob[] = []
  for await (const chunk of chunkStore.getChunkStream(recordingId)) {
    chunks.push(chunk)
  }

  return new File(chunks, recording.filename, {
    type: recording.mimeType || 'audio/webm',
  })
}

type LocalRecordingsState = {
  recordingsById: Record<string, StoredRecording>
  upsertRecording: (recording: StoredRecording) => void
  updateRecording: (
    recordingId: string,
    patch: Partial<Omit<StoredRecording, 'id' | 'createdAt'>>
  ) => void
  removeRecording: (recordingId: string) => void
  getRecordingById: (recordingId: string) => StoredRecording | null
  getAllRecordings: () => StoredRecording[]
  requestUpload: (recordingId: string) => Promise<void>
  clearFinalizationCheckpoints: () => void
}

export const useLocalRecordingsStore = create<LocalRecordingsState>()(
  persist(
    (set, get) => ({
      recordingsById: {},
      upsertRecording: (recording) =>
        set((state) => ({
          recordingsById: {
            ...state.recordingsById,
            [recording.id]: recording,
          },
        })),
      updateRecording: (recordingId, patch) =>
        set((state) => {
          const current = state.recordingsById[recordingId]
          if (!current) {
            return state
          }
          return {
            recordingsById: {
              ...state.recordingsById,
              [recordingId]: {
                ...current,
                ...patch,
                updatedAt: Date.now(),
              },
            },
          }
        }),
      removeRecording: (recordingId) =>
        set((state) => {
          if (!state.recordingsById[recordingId]) {
            return state
          }
          const next = { ...state.recordingsById }
          delete next[recordingId]
          void chunkStore.clearRecording(recordingId)
          return { recordingsById: next }
        }),
      getRecordingById: (recordingId) =>
        get().recordingsById[recordingId] ?? null,
      getAllRecordings: () => Object.values(get().recordingsById),
      clearFinalizationCheckpoints: () =>
        set((state) => ({
          recordingsById: Object.fromEntries(
            Object.entries(state.recordingsById).map(([id, recording]) => [
              id,
              { ...recording, finalizationFileId: undefined },
            ])
          ),
        })),
      requestUpload: async (recordingId) => {
        const recording = get().recordingsById[recordingId]
        if (
          !recording ||
          recording.chunkCount <= 0 ||
          recording.status === 'uploading' ||
          recording.status === 'recording' ||
          recording.status === 'paused' ||
          areUploadsPaused()
        ) {
          return
        }
        get().updateRecording(recordingId, {
          status: 'stopped',
          uploadError: null,
          uploadProgress: 0,
        })
      },
    }),
    {
      name: 'local-recordings-metadata-v1',
      partialize: (state) => ({
        recordingsById: state.recordingsById,
      }),
      // localStorage hydrates synchronously, before the store variable and upload
      // subscription exist. Recover during the merge without referencing either.
      merge: (persistedState, currentState) => {
        const saved = persistedState as
          | Partial<LocalRecordingsState>
          | undefined
        const recordingsById = Object.fromEntries(
          Object.entries(saved?.recordingsById ?? {})
            .filter(([, recording]) => recording.chunkCount > 0)
            .map(([id, recording]) => {
              const interrupted =
                recording.status === 'recording' ||
                recording.status === 'uploading' ||
                recording.status === 'paused' ||
                recording.status === 'stopped'
              return [
                id,
                {
                  ...recording,
                  ...(interrupted && {
                    status: 'exited' as const,
                    uploadProgress: recording.finalizationFileId ? 100 : 0,
                    uploadError: null,
                  }),
                  language: recording.language ?? 'fr',
                },
              ]
            })
        )
        return { ...currentState, recordingsById }
      },
    }
  )
)

function checkUploads() {
  if (areUploadsPaused()) return
  const recordings = Object.values(
    useLocalRecordingsStore.getState().recordingsById
  )
  const isUploading = recordings.some(
    (recording) =>
      recording.status === 'uploading' ||
      uploadManager.isUploading(recording.id)
  )
  if (!isUploading) {
    const nextUpload = recordings.find(
      (recording) =>
        recording.status === 'stopped' && !uploadsInProgress.has(recording.id)
    )
    if (nextUpload) {
      void uploadRecording(nextUpload.id)
    }
  }
}

useLocalRecordingsStore.subscribe((newState, prevState) => {
  if (newState.recordingsById !== prevState?.recordingsById) {
    checkUploads()
  }
})

/** Called only by an explicit user action after the upload cooldown. */
export const resumeLocalUploads = checkUploads
